import { useRef, useState } from "react";
import ClockDataCell from "./ClockDataCell";
import styled from "styled-components/macro";
import { useEventListener } from "utils/customHooks/genericHooks";
import { CUSTOM_SCROLLBAR_CSS_HORIZONTAL } from "App/CUSTOM_SCROLLBAR_CSS";
import { LinearClockLegend } from "./LinearClockLegend";
import { useTrendChartBarRanges } from "utils/customHooks/trendChartHooks";

/** min number of clocks cells to set their size to small */
const MAX_CLOCK_CELLS_BEFORE_SMALL_SIZE = 10;

const LOADING_PLACEHOLDER_RANGES = [...new Array(8)].map((_, idx) => ({
  ge: 0,
  lt: 0,
}));

/** A linear clock with output counts and output losses values for a given range */
export default function LinearClock() {
  const linearClockRef = useRef(null as any);
  const [justifyContent, setJustifyContent] = useState(null as string | null);

  // if we have not set the width yet, and both scrollWidth and offsetWidth are defined,
  // then set width, which will cause a re-render to center the content properly
  function setJustify() {
    if (
      Boolean(linearClockRef.current?.scrollWidth) &&
      Boolean(linearClockRef.current?.offsetWidth)
    ) {
      const doesOverflow =
        linearClockRef.current?.scrollWidth >
        linearClockRef.current?.offsetWidth;
      setJustifyContent(doesOverflow ? "end" : "center");
    }
  }
  if (justifyContent === null) {
    setJustify();
  }
  useEventListener("resize", setJustify);

  /** LinearClock uses trend chart data dumps and we set the
   * binSize where the <TrendChartBarsDataDumpsGetter> is rendered
   */
  const trendChartBarRanges = useTrendChartBarRanges();

  return (
    <LinearClockStyles
      ref={linearClockRef}
      justifyContent={justifyContent}
      data-component-name="Linear Clock"
    >
      <LinearClockLegend />
      <div className="clock-cells-wrapper">
        {(trendChartBarRanges.length === 0
          ? LOADING_PLACEHOLDER_RANGES
          : trendChartBarRanges
        ).map(({ ge, lt }, idx) => (
          <ClockDataCell
            key={`clock-data-cell-${idx}`}
            startTime={ge}
            endTime={lt}
            isSizeSmall={
              trendChartBarRanges.length > MAX_CLOCK_CELLS_BEFORE_SMALL_SIZE
            }
          />
        ))}
      </div>
    </LinearClockStyles>
  );
}
const LinearClockStyles = styled.div<{ justifyContent: string | null }>`
  display: flex;
  justify-content: ${props =>
    props.justifyContent === "end" ? "flex-start" : "center"};
  align-items: center;
  box-sizing: border-box;
  overflow-x: auto;
  overflow-y: hidden;
  ${CUSTOM_SCROLLBAR_CSS_HORIZONTAL}
  height: 100%;
  width: 100%;
  [class*="LinearClockLegend__LinearClockLegendStyles"] {
    margin-right: 24px;
  }
  .clock-cells-wrapper {
    display: flex;
    margin: 0 0 auto 0;
    justify-content: ${props =>
      props.justifyContent !== null ? props.justifyContent : "center"};
    transform: translateX(0px);
    transition: transform 0.2s ease-in-out;
  }
`;
